import React from "react";
import ExlyModal from "common/Components/ExlyModal";
import { Button } from "@my-scoot/component-library-legacy";
import GetAppIcon from "@material-ui/icons/GetApp";
import {
  ACTION_TYPES_ENUM,
  ACTION_TYPE_LABELS,
} from "../Transactions.constants";
import { UPDATE_HISTORY_MODAL_TEXTS } from "./UpdateHistoryModal.constants";
import { useUpdateHistoryModal } from "./UpdateHistoryModal.hooks";
import {
  getFriendlyLabel,
  formatTimestamp,
  getTagClassName,
  isValidValue,
  getChangedEntries,
  getItemAuditData,
} from "./UpdateHistoryModal.utils";
import { checkIsStringEmpty } from "features/Common/modules/DataTypes/Strings.utils";
import { checkIsArrayEmpty } from "features/Common/modules/DataTypes/utils/Array.utils";

export const UpdateHistoryModal = ({ open, onClose, record }) => {
  const { historyList, loading, handleOpenPdf } = useUpdateHistoryModal(
    open,
    record
  );

  /**
   * Renders the changes list as a clean, responsive 3-column table/grid.
   *
   * @param {Object} oldValues
   * @param {Object} newValues
   */
  const renderChangesTable = (oldValues, newValues) => {
    const changedEntries = getChangedEntries(oldValues, newValues);

    if (checkIsArrayEmpty(changedEntries)) {
      return (
        <div className="tw-text-xs tw-italic tw-text-gray-500 tw-p-4 tw-text-center">
          {UPDATE_HISTORY_MODAL_TEXTS.NO_CHANGES}
        </div>
      );
    }

    return (
      <div className="tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200">
        {/* Table/Grid Header */}
        <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-4 tw-py-2.5 tw-bg-gray-50 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200 tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wider tw-text-gray-400">
          <div>Field</div>
          <div className="tw-text-red-500">Old Value</div>
          <div className="tw-text-green-600">New Value</div>
        </div>

        {/* Change rows */}
        <div className="tw-divide-y tw-divide-gray-100 tw-divide-solid">
          {changedEntries.map((key) => {
            const oldVal = oldValues?.[key];
            const newVal = newValues?.[key];
            return (
              <div
                key={key}
                className="tw-grid tw-grid-cols-3 tw-gap-4 tw-px-4 tw-py-3 tw-text-xs tw-items-center"
              >
                <div className="tw-font-semibold tw-text-gray-700">
                  {getFriendlyLabel(key)}
                </div>
                <div className="tw-text-red-600 tw-bg-red-50/50 tw-px-2 tw-py-1 tw-rounded tw-font-mono tw-break-all">
                  {isValidValue(oldVal) ? String(oldVal) : "—"}
                </div>
                <div className="tw-text-green-700 tw-bg-green-50/50 tw-px-2 tw-py-1 tw-rounded tw-font-mono tw-break-all">
                  {isValidValue(newVal) ? String(newVal) : "—"}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * Helper to render action badges cleanly without nested inline ternaries
   */
  const renderActionBadges = (
    sortedActions,
    item,
    creditNoteUrl,
    reissuedInvoiceUrl
  ) => {
    const hasActions = !checkIsArrayEmpty(sortedActions);

    if (hasActions) {
      return sortedActions.map((action) => (
        <span
          key={`${action.action_type}-${action.created_at}`}
          className={`tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded ${getTagClassName(
            action.action_type
          )}`}
        >
          {ACTION_TYPE_LABELS[action.action_type] || action.action_label}
        </span>
      ));
    }

    if (item.action_type) {
      return (
        <span
          className={`tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded ${getTagClassName(
            item.action_type
          )}`}
        >
          {ACTION_TYPE_LABELS[item.action_type] ||
            item.action_label ||
            UPDATE_HISTORY_MODAL_TEXTS.DEFAULT_ACTION_LABEL}
        </span>
      );
    }

    const hasCreditNote = !checkIsStringEmpty(creditNoteUrl);
    const hasReissuedInvoice = !checkIsStringEmpty(reissuedInvoiceUrl);

    if (hasCreditNote || hasReissuedInvoice) {
      return (
        <>
          {hasCreditNote && (
            <span
              className={`tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded ${getTagClassName(
                ACTION_TYPES_ENUM.CREDIT_NOTE_RAISED
              )}`}
            >
              {ACTION_TYPE_LABELS[ACTION_TYPES_ENUM.CREDIT_NOTE_RAISED]}
            </span>
          )}
          {hasReissuedInvoice && (
            <span
              className={`tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded ${getTagClassName(
                ACTION_TYPES_ENUM.INVOICE_REISSUED
              )}`}
            >
              {ACTION_TYPE_LABELS[ACTION_TYPES_ENUM.INVOICE_REISSUED]}
            </span>
          )}
        </>
      );
    }

    return (
      <span
        className={`tw-text-xs tw-font-semibold tw-px-2 tw-py-1 tw-rounded ${getTagClassName(
          null
        )}`}
      >
        {UPDATE_HISTORY_MODAL_TEXTS.DEFAULT_ACTION_LABEL}
      </span>
    );
  };

  /**
   * Helper to render PDF download action buttons cleanly
   */
  const renderPdfButtons = (
    sortedActions,
    creditNoteUrl,
    reissuedInvoiceUrl
  ) => {
    const actionsWithUrl = sortedActions.filter(
      (action) => !checkIsStringEmpty(action.url)
    );
    const hasActionPdf = !checkIsArrayEmpty(actionsWithUrl);
    const hasCreditNotePdf = !checkIsStringEmpty(creditNoteUrl);
    const hasReissuedInvoicePdf = !checkIsStringEmpty(reissuedInvoiceUrl);

    const showPdfSection =
      hasActionPdf || hasCreditNotePdf || hasReissuedInvoicePdf;

    if (!showPdfSection) return null;

    return (
      <div className="tw-p-4 tw-bg-gray-50 tw-flex tw-flex-wrap tw-gap-3 tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200">
        {actionsWithUrl.map((action) => {
          const isCreditNote =
            action.action_type === ACTION_TYPES_ENUM.CREDIT_NOTE_RAISED;
          const buttonLabel = isCreditNote
            ? UPDATE_HISTORY_MODAL_TEXTS.DOWNLOAD_CREDIT_NOTE
            : UPDATE_HISTORY_MODAL_TEXTS.DOWNLOAD_INVOICE;

          return (
            <Button
              key={`${action.action_type}-${action.created_at}`}
              onClick={() => handleOpenPdf(action.url)}
              className="tw-border-none tw-flex tw-items-center !tw-py-1 !tw-px-3"
            >
              <GetAppIcon className="tw-mr-1.5 !tw-text-[16px]" />
              <span>{buttonLabel}</span>
            </Button>
          );
        })}

        {!hasActionPdf && hasCreditNotePdf && (
          <Button
            onClick={() => handleOpenPdf(creditNoteUrl)}
            className="tw-border-none tw-flex tw-items-center !tw-py-1 !tw-px-3"
          >
            <GetAppIcon className="tw-mr-1.5 !tw-text-[16px]" />
            <span>{UPDATE_HISTORY_MODAL_TEXTS.DOWNLOAD_CREDIT_NOTE}</span>
          </Button>
        )}

        {!hasActionPdf && !hasCreditNotePdf && hasReissuedInvoicePdf && (
          <Button
            onClick={() => handleOpenPdf(reissuedInvoiceUrl)}
            className="tw-border-none tw-flex tw-items-center !tw-py-1 !tw-px-3"
          >
            <GetAppIcon className="tw-mr-1.5 !tw-text-[16px]" />
            <span>{UPDATE_HISTORY_MODAL_TEXTS.DOWNLOAD_INVOICE}</span>
          </Button>
        )}
      </div>
    );
  };

  return (
    <ExlyModal
      open={open}
      title={UPDATE_HISTORY_MODAL_TEXTS.TITLE}
      onClose={onClose}
      customFooter={<></>}
      desktopModalWidth="650px"
      className="!tw-p-0"
    >
      <div className="tw-font-sans tw-p-3 sm:tw-p-6 tw-flex tw-flex-col tw-gap-4 tw-overflow-y-auto">
        {loading ? (
          /* Loading Indicator */
          <div className="tw-flex tw-items-center tw-justify-center tw-p-8">
            <span className="tw-text-sm tw-text-gray-500">
              {UPDATE_HISTORY_MODAL_TEXTS.LOADING}
            </span>
          </div>
        ) : checkIsArrayEmpty(historyList) ? (
          /* Empty State */
          <div className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300 tw-p-8">
            <span className="tw-text-sm tw-text-gray-500">
              {UPDATE_HISTORY_MODAL_TEXTS.EMPTY}
            </span>
          </div>
        ) : (
          /* Audit Logs List */
          historyList.map((item, index) => {
            const {
              oldValues,
              newValues,
              createdAt,
              sortedActions,
              creditNoteUrl,
              reissuedInvoiceUrl,
            } = getItemAuditData(item);

            return (
              <div
                key={
                  item.action_group_id ||
                  item.uuid ||
                  item.id ||
                  `history-item-${index}`
                }
                className="tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-overflow-hidden tw-mb-4"
              >
                {/* Header */}
                <div className="tw-flex tw-items-center tw-justify-between tw-bg-[#F8F9FA] tw-px-4 tw-py-3 tw-border-b tw-border-t-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-200">
                  <div className="tw-flex tw-flex-wrap tw-gap-2">
                    {renderActionBadges(
                      sortedActions,
                      item,
                      creditNoteUrl,
                      reissuedInvoiceUrl
                    )}
                  </div>
                  <span className="tw-text-xs tw-text-gray-500 tw-font-medium">
                    {formatTimestamp(createdAt)}
                  </span>
                </div>

                {/* Changes List */}
                {renderChangesTable(oldValues, newValues)}

                {/* PDF Download Links */}
                {renderPdfButtons(
                  sortedActions,
                  creditNoteUrl,
                  reissuedInvoiceUrl
                )}
              </div>
            );
          })
        )}
      </div>
    </ExlyModal>
  );
};
